import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import clsx from "clsx";
import { LayoutDashboard, Users, ShieldCheck, ScrollText, LogOut, Menu, X, Building2, ListChecks, Briefcase, FileStack, Landmark, Scale, BookOpen, Calculator, UserSquare2, Wallet, Receipt, CircleDollarSign, TrendingDown, BarChart3, Zap, ScanText, Lock, FolderKanban, Folders, FileSignature, MapPin, ChevronDown, Package, Truck, ShoppingCart, ClipboardList, Percent, Repeat, History, HandCoins, ScanBarcode, Undo2, AlertTriangle } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useLogout } from "@/api/auth";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { ApiSuccessResponse } from "@erp/shared";
import NotificationBell from "@/components/common/NotificationBell";
import { useMyNav, type NavModule } from "@/api/modules";

interface NavItem {
  to: string;
  label: string;
  permission?: string;
  icon: typeof LayoutDashboard;
}

interface NavGroup {
  label: string | null;
  items: NavItem[];
}

// Los módulos de negocio (Trámites, Impuestos, Contabilidad, etc.) agregan su
// propia entrada aquí a medida que se construyen (evita enlaces a páginas
// inexistentes, regla #57).
//
// Agrupado por módulo (a pedido explícito del usuario: "quiero que todo lo
// contable esté en un módulo, y todo lo jurídico también, y los trámites y
// las configuraciones en uno") — antes era una sola lista plana de 23
// enlaces, que se sentía como una app genérica en vez de una suite por
// áreas de una firma contable/jurídica real. Ningún enlace ni permiso
// cambió, solo cómo se agrupan visualmente en el menú.
const NAV_GROUPS: NavGroup[] = [
  {
    label: null, // sin encabezado: lo primero que se ve, uso diario transversal
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/clients", label: "Clientes", icon: Building2, permission: "clients.read" },
      { to: "/operacion", label: "Operación", icon: MapPin, permission: "clients.read" },
      { to: "/documents", label: "Documentos", icon: Folders, permission: "documents.read" },
      { to: "/document-generator", label: "Generador de Documentos", icon: FileSignature, permission: "documents.create" },
      { to: "/tasks", label: "Tareas", icon: ListChecks, permission: "tasks.read" },
      { to: "/workbenches", label: "Mesa de Trabajo", icon: FolderKanban, permission: "workbenches.read" },
    ],
  },
  {
    label: "Contabilidad y finanzas",
    items: [
      { to: "/accounting/accounts", label: "Plan de cuentas", icon: BookOpen, permission: "accounting.read" },
      { to: "/accounting/entries", label: "Asientos contables", icon: Calculator, permission: "accounting.read" },
      { to: "/invoices", label: "Facturación", icon: Receipt, permission: "invoices.read" },
      { to: "/payments", label: "Pagos", icon: CircleDollarSign, permission: "payments.read" },
      { to: "/cartera", label: "Cartera", icon: TrendingDown, permission: "invoices.read" },
      { to: "/taxes", label: "Impuestos", icon: Landmark, permission: "taxes.read" },
      { to: "/employees", label: "Empleados", icon: UserSquare2, permission: "payroll.read" },
      { to: "/payroll/runs", label: "Nómina", icon: Wallet, permission: "payroll.read" },
      { to: "/reports", label: "Reportes", icon: BarChart3, permission: "reports.read" },
    ],
  },
  {
    label: "Jurídico",
    items: [{ to: "/legal-cases", label: "Casos jurídicos", icon: Scale, permission: "legal.read" }],
  },
  {
    label: "Trámites y servicios",
    items: [
      { to: "/procedures", label: "Trámites", icon: FileStack, permission: "procedures.read" },
      { to: "/services", label: "Servicios", icon: Briefcase, permission: "services.read" },
    ],
  },
  {
    label: "Configuración",
    items: [
      { to: "/users", label: "Usuarios", icon: Users, permission: "users.read" },
      { to: "/roles", label: "Roles y permisos", icon: ShieldCheck, permission: "roles.read" },
      { to: "/audit", label: "Auditoría", icon: ScrollText, permission: "audit.read" },
      { to: "/automations", label: "Automatizaciones", icon: Zap, permission: "automations.read" },
      { to: "/ocr-review", label: "Revisión OCR", icon: ScanText, permission: "documents.approve" },
      { to: "/settings/security", label: "Seguridad", icon: Lock },
      { to: "/organizations", label: "Organizaciones", icon: Building2, permission: "organizations.create" },
    ],
  },
];

const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/clients": "Clientes",
  "/operacion": "Operación",
  "/documents": "Documentos",
  "/document-generator": "Generador de Documentos",
  "/tasks": "Tareas",
  "/services": "Servicios",
  "/procedures": "Trámites",
  "/taxes": "Impuestos",
  "/legal-cases": "Jurídico",
  "/accounting/accounts": "Plan de cuentas",
  "/accounting/entries": "Asientos contables",
  "/employees": "Empleados",
  "/payroll/runs": "Nómina",
  "/invoices": "Facturación",
  "/payments": "Pagos",
  "/cartera": "Cartera",
  "/reports": "Reportes",
  "/workbenches": "Mesa de Trabajo",
  "/users": "Usuarios",
  "/roles": "Roles y permisos",
  "/audit": "Auditoría",
  "/automations": "Automatizaciones",
  "/ocr-review": "Revisión OCR",
  "/settings/security": "Seguridad",
  "/organizations": "Organizaciones",
  "/store/products": "Productos",
  "/store/purchases": "Compras",
  "/store/suppliers": "Proveedores",
  "/store/payables": "Cuentas por pagar",
  "/store/quotes": "Cotizaciones",
  "/store/purchase-quotes": "Cotizaciones de compra",
  "/store/layaways": "Apartados",
  "/store/pos": "Punto de Venta",
  "/store/reports": "Reportes de ventas",
  "/store/stock-counts": "Toma de inventario",
  "/store/promotions": "Promociones",
  "/store/warranties": "Garantías",
  "/store/branches": "Sucursales",
  "/store/transfers": "Trasladar Productos",
  "/store/kardex": "Kardex",
  "/store/credits": "Créditos",
  "/store/customers": "Clientes de tienda",
  "/store/price-check": "Validador de precios",
  "/store/sales-history": "Historial de ventas",
  "/store/returns": "Devoluciones",
  "/store/cash-shifts": "Cierres de caja",
  "/store/shrinkage": "Mermas",
  "/store/alerts": "Alertas",
};

// Nombre de ícono (guardado en `modules.icon`, tabla del backend) -> componente de lucide-react.
// Mismo set de íconos que ya usaba el menú estático — el menú dinámico (arquitectura
// modular multi-industria, ver `module.service.ts`) reutiliza exactamente los mismos.
const ICON_MAP: Record<string, typeof LayoutDashboard> = {
  LayoutDashboard,
  Building2,
  MapPin,
  Folders,
  FileSignature,
  ListChecks,
  FolderKanban,
  BookOpen,
  Calculator,
  Receipt,
  CircleDollarSign,
  TrendingDown,
  Landmark,
  UserSquare2,
  Wallet,
  BarChart3,
  Scale,
  FileStack,
  Briefcase,
  Users,
  ShieldCheck,
  ScrollText,
  Zap,
  ScanText,
  Lock,
  Package,
  Truck,
  ShoppingCart,
  ClipboardList,
  Percent,
  Repeat,
  History,
  HandCoins,
  ScanBarcode,
  Undo2,
  AlertTriangle,
};

/** Agrupa los módulos habilitados (ya filtrados por permiso en el backend) en el mismo formato NavGroup, preservando el orden por sortOrder. */
function buildGroupsFromModules(modules: NavModule[]): NavGroup[] {
  const sorted = [...modules].sort((a, b) => a.sortOrder - b.sortOrder);
  const order: Array<string | null> = [];
  const byGroup = new Map<string | null, NavItem[]>();
  for (const m of sorted) {
    if (!byGroup.has(m.navGroup)) {
      byGroup.set(m.navGroup, []);
      order.push(m.navGroup);
    }
    byGroup.get(m.navGroup)!.push({ to: m.routePath, label: m.name, icon: ICON_MAP[m.icon ?? ""] ?? LayoutDashboard });
  }
  return order.map((label) => ({ label, items: byGroup.get(label)! }));
}

function initials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function pageTitleFor(pathname: string): string {
  const match = Object.keys(PAGE_TITLES).find((p) => pathname === p || pathname.startsWith(`${p}/`));
  return match ? PAGE_TITLES[match] : "";
}

export default function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const logout = useLogout();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const { data: organization } = useQuery({
    queryKey: ["organizations", "current"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<{ name: string }>>("/organizations/current");
      return data.data;
    },
    staleTime: 5 * 60_000,
  });

  // Menú dinámico (arquitectura modular multi-industria): mientras carga o si falla,
  // se usa el menú estático de siempre (NAV_GROUPS) para que el sidebar nunca quede
  // vacío ni rompa — cero regresión (regla explícita del usuario).
  const { data: navModules } = useMyNav();
  const groups = navModules && navModules.length > 0 ? buildGroupsFromModules(navModules) : NAV_GROUPS;

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.permission || hasPermission(item.permission)),
    }))
    .filter((group) => group.items.length > 0);

  // Grupos plegables (a pedido explícito del usuario: "que se dé clic y se
  // despliegue los otros submódulos"): por defecto cada grupo con encabezado
  // arranca cerrado, salvo el que contiene la página activa — así el menú se
  // ve compacto pero nunca "esconde" la sección donde ya estás. El grupo sin
  // encabezado (Dashboard/uso diario) siempre se muestra completo.
  const [collapsedOverride, setCollapsedOverride] = useState<Record<string, boolean>>({});

  const groupContainsActive = (group: NavGroup) =>
    group.items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  const isGroupOpen = (group: NavGroup) => {
    if (!group.label) return true;
    if (group.label in collapsedOverride) return !collapsedOverride[group.label];
    return groupContainsActive(group);
  };

  const toggleGroup = (label: string, currentlyOpen: boolean) =>
    setCollapsedOverride((prev) => ({ ...prev, [label]: currentlyOpen }));

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col transform bg-gradient-to-b from-brand-950 via-brand-900 to-brand-950 transition-transform lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center gap-2.5 border-b border-white/5 px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 shadow-sm shadow-accent-900/30">
            <Building2 className="h-4.5 w-4.5 text-brand-950" size={18} strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold leading-tight tracking-tight text-white">ERP Firma Profesional</span>
            <span className="block text-[10px] font-medium uppercase tracking-widest text-brand-400">Suite empresarial</span>
          </div>
          <button
            type="button"
            className="ml-auto shrink-0 text-brand-300 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Cerrar menú"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="mt-2 flex-1 space-y-3 overflow-y-auto px-3 pb-4">
          {visibleGroups.map((group, groupIndex) => {
            const open = isGroupOpen(group);
            return (
              <div key={group.label ?? `group-${groupIndex}`} className="space-y-0.5">
                {group.label && (
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-md px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-brand-500 hover:text-brand-300"
                    onClick={() => toggleGroup(group.label!, open)}
                    aria-expanded={open}
                  >
                    <span>{group.label}</span>
                    <ChevronDown size={12} className={clsx("transition-transform", !open && "-rotate-90")} />
                  </button>
                )}
                {open &&
                  group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          clsx(
                            "group flex items-center gap-3 rounded-lg border-l-2 px-3 py-2 text-sm font-medium transition-colors",
                            isActive
                              ? "border-accent-400 bg-white/10 text-white"
                              : "border-transparent text-brand-200 hover:border-brand-500 hover:bg-white/5 hover:text-white"
                          )
                        }
                        onClick={() => setSidebarOpen(false)}
                      >
                        {({ isActive }) => (
                          <>
                            <Icon size={18} strokeWidth={2} className={isActive ? "text-accent-400" : "text-brand-400 group-hover:text-accent-300"} />
                            {item.label}
                          </>
                        )}
                      </NavLink>
                    );
                  })}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-semibold text-brand-950">
              {initials(user?.fullName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">{user?.fullName}</p>
              <p className="truncate text-xs text-brand-400">{user?.roles.join(", ")}</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-md p-1.5 text-brand-400 hover:bg-white/10 hover:text-white"
              onClick={() => logout.mutate()}
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="flex h-16 items-center gap-4 border-b border-gray-200/80 bg-white px-4 shadow-sm shadow-gray-900/[0.02] lg:px-6">
          <button
            type="button"
            className="text-gray-500 lg:hidden"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            <Menu size={22} />
          </button>
          <div>
            <p className="section-eyebrow hidden sm:block">{organization?.name ?? "Panel"}</p>
            <h1 className="text-base font-semibold text-gray-900">{pageTitleFor(location.pathname)}</h1>
          </div>
          <div className="flex-1" />
          {organization && (
            <span className="hidden items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 sm:inline-flex">
              <Building2 size={12} />
              {organization.name}
            </span>
          )}
          {hasPermission("notifications.read") && <NotificationBell />}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-semibold text-brand-950" title={user?.fullName}>
            {initials(user?.fullName)}
          </div>
        </header>

        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
